import { Button, Col, Row, Tooltip, Typography } from 'antd';
import { head, values } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { generatePath, useHistory, useRouteMatch } from 'react-router';

import { CREATE_SHIPMENT_MUTATION } from '../../app/graphql/jobFormQueries';
import routes from '../../app/routes';
import { useMountEffect } from '../../common/utils/hookUtils';
import { FAIcon } from '../../components/adapters/fontAwesomeAdapters';
import { CustomLoaderButton } from '../../components/buttons/buttons';
import { useAsynchronousAction } from '../../components/data/asyncActionElements';
import SimpleDialog, {
  SimpleDialogLabel,
} from '../../components/dialogs/SimpleDialog';
import { useAccountOptions } from '../../components/domainSpecific/accountElements';
import { NoLabelForm } from '../../components/forms/forms';
import { FormItemSelect } from '../../components/forms/selects';
import { useResponsiveQueries } from '../../components/responsive/responsiveQueries';
import { InlineLink } from '../../components/typography';
import { forceSavedShipmentForm } from '../../forms/shipments/newShipment/savedShipmentsUtils';
import logoImg from '../../resources/img/quick-online-logo-q.svg';
import {
  BaseShipmentSubmitStateDialog,
  useShipmentSubmitState,
} from './useShipmentSubmitState';
import { useTooltipStyles } from '../../styles/tooltip.styles';
import { useAccountElementsStyles } from '../../components/domainSpecific/accountElements.styles';
import { useNewShipmentDialogsStyles } from './newShipmentDialogs.styles';
import { useConfirmOrderButtonsStyles } from './confirmOrderButtons.styles';

export function AccountSelectionDialog({
  value,
  onChange,
  customerAccounts,
  titleId = 'book.newShipment.accountDialog.title',
  filterFunc = account => account.newOrder.enabled,
}) {
  useMountEffect(() => {
    if (customerAccounts.length === 1) {
      onChange(customerAccounts[0]);
    }
  }, [onChange, customerAccounts]);

  const { options: accountOptions } = useAccountOptions({
    filter: filterFunc,
  });
  const { styles } = useAccountElementsStyles();
  const { styles: newShipmentDialogsStyles } = useNewShipmentDialogsStyles();

  return (
    <SimpleDialog
      className={newShipmentDialogsStyles.newShipmentSelectAccountDialog}
      data-subject="new-shipment"
      data-role="select-account-dialog"
      icon={<img src={logoImg} alt="" />}
      open={!value}
      centered
      mask
    >
      <Typography.Title level={1} className="sm margin-md">
        <FormattedMessage id={titleId} />
      </Typography.Title>
      <SimpleDialogLabel>
        <FormattedMessage id="book.newShipment.accountDialog.subtitle" />
      </SimpleDialogLabel>
      <NoLabelForm
        onValuesChange={ch => onChange(head(values(ch)))}
        className="no-margin-form-items"
      >
        <FormItemSelect
          name="account"
          labelId="book.newShipment.accountDialog.account"
          rules={[{ required: true }]}
          options={accountOptions}
          optionKey="number"
          formItemComponentProps={{
            classNames: { popup: { root: styles.accountSelectDropdown } },
          }}
        />
      </NoLabelForm>
      <InlineLink
        className="cancel-button"
        textId="buttons.cancel"
        to={routes.monitor}
      />
    </SimpleDialog>
  );
}

export function useNewShipmentSubmit({ referenceNo }) {
  return useShipmentSubmitState({
    mutation: CREATE_SHIPMENT_MUTATION,
    quoteReference: referenceNo,
  });
}

function ButtonWrapper({ tooltipId, children }) {
  const { styles } = useTooltipStyles();

  return tooltipId ? (
    <Tooltip
      title={<FormattedMessage id={tooltipId} />}
      classNames={{ root: styles.customLines }}
    >
      <div>{children}</div>
    </Tooltip>
  ) : (
    children
  );
}

function ConfirmOrderHeaderTBD({ action, onCancel }) {
  const media = useResponsiveQueries();
  const textId =
    media.xs || media.sm
      ? 'book.confirmOrder.saveShipment'
      : 'book.confirmOrder.saveShipment.long';
  const { styles } = useConfirmOrderButtonsStyles();

  return (
    <Row className={styles.confirmOrderButtons}>
      <Col className="ant-col__big">
        <ButtonWrapper tooltipId="book.confirmOrder.tbdWarning">
          <CustomLoaderButton
            className="ant-btn--round-left ant-btn__font-plus-2"
            type="primary"
            size="large"
            onClick={action.execute}
            data-subject="new-shipment"
            data-action="createSaved"
          >
            <FormattedMessage id={textId} />
          </CustomLoaderButton>
        </ButtonWrapper>
      </Col>
      <Col className="ant-col__small">
        <Button
          className="ant-btn--round-right"
          size="large"
          onClick={onCancel}
          disabled={action.loading}
          data-subject="new-shipment"
          data-action="cancel"
        >
          <FormattedMessage id="buttons.edit" />
        </Button>
      </Col>
    </Row>
  );
}

function ConfirmOrderHeaderNormal({ action, onCancel }) {
  const saveTextId = 'buttons.save';
  const isAirRetrieval = !!useRouteMatch(routes.book.newAirRetrieval);
  const { styles } = useConfirmOrderButtonsStyles();

  return (
    <Row className={styles.confirmOrderButtons}>
      <Col className="ant-col__small">
        <CustomLoaderButton
          className="ant-btn--round-left"
          size="large"
          disabled={isAirRetrieval}
          onClick={() => action.execute({ forceTbd: true })}
          data-subject="new-shipment"
          data-action="createSaved"
        >
          <FormattedMessage id={saveTextId} />
        </CustomLoaderButton>
      </Col>
      <Col className="ant-col__big">
        <CustomLoaderButton
          className="ant-btn__font-plus-2"
          type="primary"
          size="large"
          onClick={() => action.execute()}
          data-subject="new-shipment"
          data-action="save"
        >
          <FormattedMessage id="book.confirmOrder.confirmShipment" />
        </CustomLoaderButton>
      </Col>
      <Col className="ant-col__small">
        <Button
          className="ant-btn--round-right"
          size="large"
          onClick={onCancel}
          disabled={action.loading}
          data-subject="new-shipment"
          data-action="cancel"
        >
          <FormattedMessage id="buttons.edit" />
        </Button>
      </Col>
    </Row>
  );
}

function ConfirmOrderHeaderLoading() {
  const { styles } = useConfirmOrderButtonsStyles();
  return (
    <Row className={styles.confirmOrderButtons}>
      <Col className="ant-col__big">
        <CustomLoaderButton
          className="three-dots-lg ant-btn--round-left ant-btn--round-right ant-btn__font-plus-2"
          type="primary"
          size="large"
          loading
          disabled
        >
          <FormattedMessage id="book.confirmOrder.processing" />
        </CustomLoaderButton>
      </Col>
    </Row>
  );
}

function ConfirmOrderHeader({ shipment, action, onCancel }) {
  if (action.loading) {
    return <ConfirmOrderHeaderLoading action={action} onCancel={onCancel} />;
  }
  if (shipment.containsTbd) {
    return <ConfirmOrderHeaderTBD action={action} onCancel={onCancel} />;
  }
  return <ConfirmOrderHeaderNormal action={action} onCancel={onCancel} />;
}

export function NewShipmentSubmitDialog({
  onConfirm,
  savedShipmentModeActive,
  ...rest
}) {
  const { push } = useHistory();

  const action = useAsynchronousAction(
    async ({ shipmentForm }, { forceTbd = false } = {}) => {
      const result = await onConfirm({
        shipmentForm: forceTbd
          ? forceSavedShipmentForm(shipmentForm)
          : shipmentForm,
      });
      if (result) {
        const { jobNumber } = result;
        push(generatePath(routes.shipmentDetailSuccess, { jobNumber }));
      }
    }
  );

  const confirmOrderPageProps = {
    headerComponent: ConfirmOrderHeader,
    action,
  };

  return (
    <BaseShipmentSubmitStateDialog
      onConfirm={onConfirm}
      confirmOrderPageProps={confirmOrderPageProps}
      optimizingRouteDialogProps={
        savedShipmentModeActive
          ? {
              preparingLabelId:
                'book.newShipment.submit.preparingSavedShipment',
            }
          : {}
      }
      {...rest}
    />
  );
}

export function OrderSuccessDialog({
  onOk,
  titleId = 'book.newShipment.successDialog.title',
  subTitleId = 'book.newShipment.successDialog.subtitle',
}) {
  const { styles } = useNewShipmentDialogsStyles();
  return (
    <SimpleDialog
      icon={<FAIcon icon="check-circle" />}
      className={styles.orderSuccessDialog}
      centered
      mask
    >
      <Typography.Title level={1}>
        <FormattedMessage id={titleId} />
      </Typography.Title>
      <Typography.Title level={4} className="margin-lg">
        <FormattedMessage id={subTitleId} />
      </Typography.Title>
      <div className="margin-hor-norm3" style={{ alignSelf: 'stretch' }}>
        <Button
          type="primary"
          size="large"
          onClick={onOk}
          data-subject="order-success"
          data-action="ok"
        >
          <FormattedMessage id="buttons.ok" />
        </Button>
      </div>
    </SimpleDialog>
  );
}
